import { useState, useEffect } from 'react';
import { SOLUTION_TYPES, BADGES } from '@/data/constants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ProgressRing } from '@/components/ui/progress-ring';
import ModuleOnboarding from '@/components/ModuleOnboarding';
import { metricsOnboardingSteps, metricsOnboardingBenefits } from '@/data/onboardingConfigs';
import { 
  BarChart3, Target, CheckCircle2, Clock, Trophy, Flame, TrendingUp,
  Award, Layers, FileCheck, BookOpen, Sparkles, HelpCircle, Calendar, Loader2
} from 'lucide-react';
import { 
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, BarChart, Bar, XAxis, YAxis, Tooltip, Cell
} from 'recharts';
import { useDeliverables, useDiagnostic, useStreak, useBadges, useCohortEnrollment, useContentProgress } from '@/hooks/useParticipantData';
import { differenceInDays } from 'date-fns';

const METRICS_ONBOARDING_KEY = 'metrics_onboarding_completed';

const levelInfo: Record<string, { number: number; color: string }> = {
  'Iniciador': { number: 1, color: 'hsl(var(--chart-1))' },
  'Explorador': { number: 2, color: 'hsl(var(--chart-2))' },
  'Experimentador': { number: 3, color: 'hsl(var(--chart-3))' },
  'Implementador': { number: 4, color: 'hsl(var(--chart-4))' },
  'Integrador': { number: 5, color: 'hsl(var(--chart-5))' },
  'Transformador': { number: 6, color: 'hsl(var(--primary))' },
};

const maturityLevelNames: Record<number, string> = {
  1: 'Iniciador', 2: 'Explorador', 3: 'Experimentador',
  4: 'Implementador', 5: 'Integrador', 6: 'Transformador',
};

export default function MetricsPage() {
  const { data: deliverables = [], isLoading: loadingDeliverables } = useDeliverables();
  const { data: diagnostic, isLoading: loadingDiagnostic } = useDiagnostic();
  const { data: streak } = useStreak();
  const { data: badges = [] } = useBadges();
  const { data: enrollment } = useCohortEnrollment();
  const { data: contentProgress } = useContentProgress();

  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(METRICS_ONBOARDING_KEY);
    if (!completed) setShowOnboarding(true);
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem(METRICS_ONBOARDING_KEY, 'true');
    setShowOnboarding(false);
  };

  const handleShowOnboarding = () => {
    localStorage.removeItem(METRICS_ONBOARDING_KEY);
    setShowOnboarding(true);
  };

  // Calculate metrics from real data
  const totalDeliverables = deliverables.length;
  const validatedDeliverables = deliverables.filter(d => d.status === 'validated').length;
  
  // Current solution type: highest solution_type with validated deliverables + 1 (capped at 5)
  const validatedTypes = deliverables
    .filter(d => d.status === 'validated' && d.solution_type)
    .map(d => d.solution_type as number);
  const maxValidated = validatedTypes.length > 0 ? Math.max(...validatedTypes) : 0;
  const currentSolutionType = Math.min(maxValidated + 1, 5);
  const completedModules = Math.max(0, currentSolutionType - 1);
  const totalModules = 5;

  // Overall progress: weighted combination of content + deliverables
  const overallProgress = contentProgress 
    ? Math.round((contentProgress.percent * 0.5) + ((validatedDeliverables / Math.max(totalDeliverables, 1)) * 100 * 0.5))
    : totalDeliverables > 0 ? Math.round((validatedDeliverables / totalDeliverables) * 100) : 0;

  const streakDays = streak?.current_streak ?? 0;
  const badgeKeys = badges.map(b => b.badge_key);
  const badgesEarned = badgeKeys.length;
  const totalBadges = Object.keys(BADGES).length;

  // Demoday info
  const cohort = enrollment?.cohorts as any;
  const demoDayDate = cohort?.demo_day_date;
  const daysUntilDemoday = demoDayDate ? differenceInDays(new Date(demoDayDate), new Date()) : null;
  const canAccessDemoday = completedModules >= 5;

  // Deliverables by solution type
  const deliverablesBySolution = Object.keys(SOLUTION_TYPES).map(key => {
    const st = parseInt(key) as keyof typeof SOLUTION_TYPES;
    const count = deliverables.filter(d => d.solution_type === st).length;
    const validated = deliverables.filter(d => d.solution_type === st && d.status === 'validated').length;
    return {
      name: SOLUTION_TYPES[st].name.split(' ')[0],
      fullName: SOLUTION_TYPES[st].name,
      total: count,
      validated,
      icon: SOLUTION_TYPES[st].icon,
    };
  });

  // Radar chart data
  const radarData = [
    { skill: 'Contenido', value: deliverables.filter(d => d.solution_type === 1 && d.status === 'validated').length * 25, fullMark: 100 },
    { skill: 'Coding', value: deliverables.filter(d => d.solution_type === 2 && d.status === 'validated').length * 25, fullMark: 100 },
    { skill: 'Asistentes', value: deliverables.filter(d => d.solution_type === 3 && d.status === 'validated').length * 25, fullMark: 100 },
    { skill: 'Automatización', value: deliverables.filter(d => d.solution_type === 4 && d.status === 'validated').length * 25, fullMark: 100 },
    { skill: 'Datos', value: deliverables.filter(d => d.solution_type === 5 && d.status === 'validated').length * 25, fullMark: 100 },
  ];

  const chartColors = [
    'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))',
    'hsl(var(--chart-4))', 'hsl(var(--chart-5))',
  ];

  // Diagnostic display data
  const diagnosticLevel = diagnostic?.maturity_level;
  const diagnosticLevelName = diagnosticLevel ? (maturityLevelNames[diagnosticLevel] || `Nivel ${diagnosticLevel}`) : null;
  const diagnosticScores = diagnostic?.dimension_scores as Record<string, number> | null;
  const diagnosticPercentage = diagnosticLevel ? Math.round((diagnosticLevel / 6) * 100) : 0;

  if (showOnboarding) {
    return (
      <ModuleOnboarding
        steps={metricsOnboardingSteps}
        benefits={metricsOnboardingBenefits}
        onComplete={handleOnboardingComplete}
        ctaText="Ver mis Métricas"
      />
    );
  }

  if (loadingDeliverables || loadingDiagnostic) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl gradient-primary flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-primary-foreground" />
            </div>
            Mis Métricas
          </h1>
          <p className="text-muted-foreground mt-1">
            Tu progreso personal en la ruta LÍDER IA
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={handleShowOnboarding} className="text-muted-foreground hover:text-primary" title="Ver introducción del módulo">
          <HelpCircle className="w-5 h-5" />
        </Button>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card border-primary/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-foreground">{overallProgress}%</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Progreso General</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-success/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-foreground">{validatedDeliverables}/{totalDeliverables}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Entregables Validados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-accent/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-foreground">{completedModules}/{totalModules}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Módulos Completados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-warning/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-warning/10 flex items-center justify-center shrink-0">
                <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-warning" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-foreground">{streakDays}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Días en Racha</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Overview + Skills Radar */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Resumen de Progreso
            </CardTitle>
            <CardDescription>Tu avance hacia el Demoday</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-6">
            <ProgressRing progress={overallProgress} size={160} strokeWidth={12} />
            <div className="text-center">
              <p className="text-4xl font-bold text-foreground">{overallProgress}%</p>
              <p className="text-sm text-muted-foreground">Completado</p>
            </div>
            <div className="w-full space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Días hasta Demoday</span>
                </div>
                <Badge variant="outline" className="font-bold">
                  {daysUntilDemoday !== null ? `${daysUntilDemoday} días` : 'Sin fecha'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Acceso a Demoday</span>
                </div>
                <Badge variant={canAccessDemoday ? "default" : "secondary"} className="font-medium">
                  {canAccessDemoday ? "Habilitado" : "Pendiente"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent" />
              Mapa de Habilidades
            </CardTitle>
            <CardDescription>Dominio por tipo de solución</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="skill" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                  <Radar name="Progreso" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Diagnostic Level + Deliverables by Module */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Nivel Diagnóstico
            </CardTitle>
            <CardDescription>Tu nivel de madurez en IA</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnostic && diagnosticLevelName ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl"
                    style={{ backgroundColor: levelInfo[diagnosticLevelName]?.color || 'hsl(var(--primary))' }}
                  >
                    {levelInfo[diagnosticLevelName]?.number || diagnosticLevel}
                  </div>
                  <div>
                    <p className="text-xl font-bold text-foreground">{diagnosticLevelName}</p>
                    <p className="text-sm text-muted-foreground">
                      Nivel de madurez: {diagnosticLevel}/6
                    </p>
                  </div>
                </div>
                <Progress value={diagnosticPercentage} className="h-3" />
                <p className="text-sm text-muted-foreground text-center">
                  {diagnosticPercentage}% de madurez en IA
                </p>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-2">No has completado el diagnóstico</p>
                <Button variant="outline" size="sm">
                  Realizar Diagnóstico
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-success" />
              Entregables por Módulo
            </CardTitle>
            <CardDescription>Distribución de tus entregas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliverablesBySolution} layout="vertical">
                  <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} width={80} />
                  <Tooltip 
                    formatter={(value, name) => [value, name === 'total' ? 'Total' : 'Validados']}
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderRadius: '8px' }}
                  />
                  <Bar dataKey="validated" name="Validados" stackId="a" fill="hsl(var(--success))" radius={[0, 0, 0, 0]}>
                    {deliverablesBySolution.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Badges Section */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-warning" />
            Mis Insignias
          </CardTitle>
          <CardDescription>
            Has ganado {badgesEarned} de {totalBadges} insignias disponibles
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Object.entries(BADGES).map(([key, badge]) => {
              const isEarned = badgeKeys.includes(key);
              return (
                <div 
                  key={key}
                  className={`p-4 rounded-xl border text-center transition-all duration-300 ${
                    isEarned 
                      ? 'bg-warning/10 border-warning/30 shadow-lg shadow-warning/10' 
                      : 'bg-muted/30 border-border/50 opacity-50 grayscale'
                  }`}
                >
                  <div className="text-3xl mb-2">{badge.icon}</div>
                  <p className={`text-sm font-medium ${isEarned ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {badge.name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {badge.description}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Deliverables Timeline */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Historial de Entregables
          </CardTitle>
          <CardDescription>Tus entregas recientes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {deliverables.length > 0 ? (
              deliverables.map((deliverable) => {
                const st = (deliverable.solution_type || 1) as keyof typeof SOLUTION_TYPES;
                const solutionInfo = SOLUTION_TYPES[st] || SOLUTION_TYPES[1];
                return (
                  <div 
                    key={deliverable.id}
                    className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 border border-border/50"
                  >
                    <div className="text-2xl">{solutionInfo.icon}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{deliverable.name}</p>
                      <p className="text-sm text-muted-foreground">{solutionInfo.name}</p>
                    </div>
                    <Badge 
                      variant={
                        deliverable.status === 'validated' ? 'default' :
                        deliverable.status === 'submitted' ? 'secondary' :
                        deliverable.status === 'in_progress' ? 'outline' : 'secondary'
                      }
                      className={deliverable.status === 'validated' ? 'bg-success text-success-foreground' : ''}
                    >
                      {deliverable.status === 'validated' && 'Validado'}
                      {deliverable.status === 'submitted' && 'Enviado'}
                      {deliverable.status === 'in_progress' && 'En Progreso'}
                      {deliverable.status === 'not_started' && 'No Iniciado'}
                      {deliverable.status === 'rejected' && 'Revisar'}
                    </Badge>
                    {deliverable.submitted_at && (
                      <span className="text-xs text-muted-foreground hidden sm:block">
                        {new Date(deliverable.submitted_at).toLocaleDateString('es-MX')}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <FileCheck className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No tienes entregables aún</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
